import { EntitiesToolbar } from "@components/common/toolbar/entities-toolbar";
import { DEFAULT_TEAMS_SORT_BY } from "@components/teams/teams-constants";
import { TeamsSort } from "@components/teams/teams-sort";
import { BaseCompanyIdProps } from "@custom-types/base-types";
import { TeamsSortBy } from "@custom-types/teams-types";
import { isTeamsSortBy } from "@custom-types/type-guards";
import { useMediaQueryList } from "@hooks/use-media-query";
import { useSubjectsSortByQuery } from "@hooks/use-subjects-sort-by-query";
import { TeamsDetails } from "@pages/members/teams/teams-details";
import { useTeamsPageBulkActions } from "@pages/members/teams/use-teams-page-bulk-actions";
import { TeamsTableWrapper } from "@pages/members/teams/teams-table-wrapper";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import {
  fetchingTeamsFlagsSelector,
  teamsSelector,
} from "@store/teams/teams-selector";
import { fetchTeams } from "@store/teams/teams-slice-thunk";
import { getExportComponent } from "@utils/member-utils";
import { sortTeams } from "@utils/team-utils";
import { useEffect, useMemo } from "react";

/**
 * The component that will render the kpi details and the table for the teams
 */
export function Teams({ companyId }: BaseCompanyIdProps): JSX.Element {
  const teams = useAppSelector(teamsSelector);
  const { status } = useAppSelector(fetchingTeamsFlagsSelector);
  const dispatch = useAppDispatch();
  const { isScreenSmOrSmaller } = useMediaQueryList();

  /** Whether the table is in loading state */
  const isLoading = status === "pending";
  const { sortBy, setSortBy } = useSubjectsSortByQuery<TeamsSortBy>({
    defaultSort: DEFAULT_TEAMS_SORT_BY,
    isValidSort: isTeamsSortBy,
  });

  // Sort the teams client-side
  const sortedTeams = useMemo(() => sortTeams(teams, sortBy), [teams, sortBy]);

  // Fetches searched teams
  useEffect(() => {
    async function searchTeams(): Promise<void> {
      await dispatch(
        fetchTeams({
          searchText: "",
        })
      );
    }

    void searchTeams();
  }, [dispatch]);

  const buttonComponents = useTeamsPageBulkActions({
    companyId,
    teams,
  });

  const shouldRenderActions = sortedTeams.length > 0;

  return (
    <>
      {/* Hiding KPI bar on small screens */}
      {!isScreenSmOrSmaller && <TeamsDetails />}

      <EntitiesToolbar
        toolbarEntityName="Team"
        numberOfRows={teams.length}
        isLoading={isLoading}
        sortActions={
          shouldRenderActions && (
            <TeamsSort key="teams-sort" sortBy={sortBy} setSortBy={setSortBy} />
          )
        }
        exportComponent={getExportComponent(
          teams.length,
          buttonComponents.export
        )}
      />

      <TeamsTableWrapper
        companyId={companyId}
        sortedTeams={sortedTeams}
        buttonComponents={buttonComponents}
        sortBy={sortBy}
        setSortBy={setSortBy}
      />
    </>
  );
}
